import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  MessageSquare, Upload, X, AlertCircle, Info, Clock
} from 'lucide-react';
import Navbar from '../components/layout/Navbar';
import Footer from '../components/layout/Footer';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Label } from '../components/ui/label';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import api from '../utils/api';
import { getUser } from '../utils/auth';
import { toast } from 'sonner';

const CreateTicketPage = () => {
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const user = getUser();
  
  const [loading, setLoading] = useState(false);
  const [cases, setCases] = useState([]);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    ticket_type: 'general',
    priority: 'normal',
    case_id: null,
    attachments: []
  });
  const [linkedToCase, setLinkedToCase] = useState('no');
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (!user || user.role !== 'client') {
      navigate('/login');
      return;
    }
    fetchUserCases();
  }, []);

  const fetchUserCases = async () => {
    try {
      const response = await api.get(`/cases?client_id=${user.id}`);
      setCases(response.data);
    } catch (error) {
      console.error('Failed to load cases:', error);
    }
  };

  // SLA Configuration (in hours)
  const SLA_CONFIG = {
    urgent: 2,
    high: 8,
    normal: 24,
    low: 48
  };

  // Ticket Types
  const ticketTypes = [
    { value: 'inquiry', labelAr: 'استفسار', labelEn: 'Inquiry' },
    { value: 'complaint', labelAr: 'شكوى', labelEn: 'Complaint' },
    { value: 'request_clarification', labelAr: 'طلب توضيح', labelEn: 'Request Clarification' },
    { value: 'additional_documents', labelAr: 'طلب مستند', labelEn: 'Additional Documents' },
    { value: 'request_appointment', labelAr: 'طلب موعد', labelEn: 'Request Appointment' },
    { value: 'general', labelAr: 'عام', labelEn: 'General' }
  ];

  const priorities = [
    { value: 'urgent', labelAr: 'عاجل', labelEn: 'Urgent', color: 'text-red-600', bgColor: 'bg-red-50', borderColor: 'border-red-200' },
    { value: 'high', labelAr: 'عالي', labelEn: 'High', color: 'text-orange-600', bgColor: 'bg-orange-50', borderColor: 'border-orange-200' },
    { value: 'normal', labelAr: 'عادي', labelEn: 'Normal', color: 'text-blue-600', bgColor: 'bg-blue-50', borderColor: 'border-blue-200' },
    { value: 'low', labelAr: 'منخفض', labelEn: 'Low', color: 'text-gray-600', bgColor: 'bg-gray-50', borderColor: 'border-gray-200' }
  ];

  const getSLAText = (priority) => {
    const hours = SLA_CONFIG[priority];
    return i18n.language === 'ar' 
      ? `${hours} ساعة` 
      : `${hours} hours`;
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  const handleLinkedToCaseChange = (value) => {
    setLinkedToCase(value);
    if (value === 'no') {
      setFormData(prev => ({ ...prev, case_id: null }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.title.trim()) {
      newErrors.title = i18n.language === 'ar' ? 'العنوان مطلوب' : 'Title is required';
    }
    
    if (!formData.description.trim()) {
      newErrors.description = i18n.language === 'ar' ? 'الوصف مطلوب' : 'Description is required';
    }
    
    if (linkedToCase === 'yes' && !formData.case_id) {
      newErrors.case_id = i18n.language === 'ar' ? 'يرجى اختيار قضية' : 'Please select a case';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      toast.error(i18n.language === 'ar' ? 'يرجى تصحيح الأخطاء في النموذج' : 'Please fix errors in the form');
      return;
    }
    
    setLoading(true);
    try {
      const submitData = {
        ...formData,
        case_id: linkedToCase === 'yes' ? formData.case_id : null
      };
      
      const response = await api.post('/tickets-v2', submitData);
      toast.success(i18n.language === 'ar' ? 'تم إنشاء التذكرة بنجاح' : 'Ticket created successfully');
      navigate(`/tickets/${response.data.id}`);
    } catch (error) {
      toast.error(i18n.language === 'ar' ? 'فشل في إنشاء التذكرة' : 'Failed to create ticket');
    } finally {
      setLoading(false);
    }
  };

  const selectedPriority = priorities.find(p => p.value === formData.priority);

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      <Navbar />

      <main className="pt-24 pb-12 px-4">
        <div className="max-w-4xl mx-auto space-y-6">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-blue-500 to-blue-600 rounded-2xl mb-4">
              <MessageSquare className="w-8 h-8 text-white" />
            </div>
            <h1 className="text-4xl font-bold mb-2">
              {i18n.language === 'ar' ? 'إنشاء تذكرة جديدة' : 'Create New Ticket'}
            </h1>
            <p className="text-gray-600 text-lg">
              {i18n.language === 'ar' ? 'املأ النموذج أدناه لإنشاء تذكرة دعم جديدة' : 'Fill out the form below to create a new support ticket'}
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Ticket Type */}
            <Card className="shadow-lg">
              <CardHeader>
                <CardTitle className="text-xl">
                  {i18n.language === 'ar' ? '1. نوع التذكرة' : '1. Ticket Type'}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <Select value={formData.ticket_type} onValueChange={(val) => handleInputChange('ticket_type', val)}>
                  <SelectTrigger className="w-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {ticketTypes.map((type) => (
                      <SelectItem key={type.value} value={type.value}>
                        {i18n.language === 'ar' ? type.labelAr : type.labelEn}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </CardContent>
            </Card>

            {/* Basic Info */}
            <Card className="shadow-lg">
              <CardHeader>
                <CardTitle className="text-xl">
                  {i18n.language === 'ar' ? '2. معلومات التذكرة' : '2. Ticket Information'}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label htmlFor="title" className="text-base font-semibold mb-2 block">
                    {i18n.language === 'ar' ? 'العنوان' : 'Title'}
                    <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="title"
                    value={formData.title}
                    onChange={(e) => handleInputChange('title', e.target.value)}
                    placeholder={i18n.language === 'ar' ? 'عنوان مختصر للتذكرة' : 'Short title for the ticket'}
                    className={errors.title ? 'border-red-500' : ''}
                  />
                  {errors.title && (
                    <p className="text-red-500 text-sm mt-1 flex items-center gap-1">
                      <AlertCircle className="w-4 h-4" />
                      {errors.title}
                    </p>
                  )}
                </div>

                <div>
                  <Label htmlFor="description" className="text-base font-semibold mb-2 block">
                    {i18n.language === 'ar' ? 'الوصف' : 'Description'}
                    <span className="text-red-500">*</span>
                  </Label>
                  <Textarea
                    id="description"
                    rows={6}
                    value={formData.description}
                    onChange={(e) => handleInputChange('description', e.target.value)}
                    placeholder={i18n.language === 'ar' ? 'وصف تفصيلي للمشكلة أو الاستفسار...' : 'Detailed description of the issue or inquiry...'}
                    className={errors.description ? 'border-red-500' : ''}
                  />
                  {errors.description && (
                    <p className="text-red-500 text-sm mt-1 flex items-center gap-1">
                      <AlertCircle className="w-4 h-4" />
                      {errors.description}
                    </p>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Link to Case */}
            <Card className="shadow-lg">
              <CardHeader>
                <CardTitle className="text-xl">
                  {i18n.language === 'ar' ? '3. ربط بقضية' : '3. Link to Case'}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label className="text-base font-semibold mb-3 block">
                    {i18n.language === 'ar' ? 'هل التذكرة مرتبطة بقضية؟' : 'Is the ticket related to a case?'}
                  </Label>
                  <RadioGroup value={linkedToCase} onValueChange={handleLinkedToCaseChange}>
                    <div className="flex items-center space-x-2 space-x-reverse">
                      <RadioGroupItem value="no" id="no-case" />
                      <Label htmlFor="no-case" className="cursor-pointer font-normal">
                        {i18n.language === 'ar' ? 'لا - تذكرة عامة' : 'No - General Ticket'}
                      </Label>
                    </div>
                    <div className="flex items-center space-x-2 space-x-reverse">
                      <RadioGroupItem value="yes" id="yes-case" />
                      <Label htmlFor="yes-case" className="cursor-pointer font-normal">
                        {i18n.language === 'ar' ? 'نعم - مرتبطة بقضية' : 'Yes - Linked to Case'}
                      </Label>
                    </div>
                  </RadioGroup>
                </div>

                {linkedToCase === 'yes' && (
                  <div>
                    <Label htmlFor="case" className="text-base font-semibold mb-2 block">
                      {i18n.language === 'ar' ? 'اختر القضية' : 'Select Case'}
                      <span className="text-red-500">*</span>
                    </Label>
                    {cases.length === 0 ? (
                      <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                        <p className="text-yellow-800 flex items-center gap-2">
                          <Info className="w-5 h-5" />
                          {i18n.language === 'ar' ? 'لا توجد قضايا متاحة لديك' : 'You have no available cases'}
                        </p>
                      </div>
                    ) : (
                      <Select value={formData.case_id || ''} onValueChange={(val) => handleInputChange('case_id', val)}>
                        <SelectTrigger className={errors.case_id ? 'border-red-500' : ''}>
                          <SelectValue placeholder={i18n.language === 'ar' ? 'اختر قضية...' : 'Select a case...'} />
                        </SelectTrigger>
                        <SelectContent>
                          {cases.map((caseItem) => (
                            <SelectItem key={caseItem.id} value={caseItem.id}>
                              {caseItem.case_number} - {caseItem.title}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                    {errors.case_id && (
                      <p className="text-red-500 text-sm mt-1 flex items-center gap-1">
                        <AlertCircle className="w-4 h-4" />
                        {errors.case_id}
                      </p>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Priority with SLA */}
            <Card className="shadow-lg">
              <CardHeader>
                <CardTitle className="text-xl">
                  {i18n.language === 'ar' ? '4. الأولوية ومدة الاستجابة (SLA)' : '4. Priority & Response Time (SLA)'}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {priorities.map((priority) => (
                    <div
                      key={priority.value}
                      onClick={() => handleInputChange('priority', priority.value)}
                      className={`p-4 border-2 rounded-xl cursor-pointer transition-all ${
                        formData.priority === priority.value
                          ? `${priority.borderColor} ${priority.bgColor}`
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <h4 className={`font-bold text-lg ${formData.priority === priority.value ? priority.color : 'text-gray-700'}`}>
                          {i18n.language === 'ar' ? priority.labelAr : priority.labelEn}
                        </h4>
                        {formData.priority === priority.value && (
                          <div className={`w-5 h-5 rounded-full ${priority.color.replace('text-', 'bg-')} flex items-center justify-center`}>
                            <div className="w-2 h-2 bg-white rounded-full" />
                          </div>
                        )}
                      </div>
                      <div className="flex items-center gap-2 text-sm">
                        <Clock className="w-4 h-4 text-gray-500" />
                        <span className="text-gray-600">
                          {i18n.language === 'ar' ? 'وقت الاستجابة: ' : 'Response time: '}
                          <span className="font-semibold">{getSLAText(priority.value)}</span>
                        </span>
                      </div>
                    </div>
                  ))}
                </div>

                {/* SLA Info Box */}
                <div className={`p-4 rounded-xl border-2 ${selectedPriority?.borderColor} ${selectedPriority?.bgColor}`}>
                  <div className="flex items-start gap-3">
                    <Info className={`w-5 h-5 mt-0.5 ${selectedPriority?.color}`} />
                    <div>
                      <h5 className={`font-bold mb-1 ${selectedPriority?.color}`}>
                        {i18n.language === 'ar' ? 'معلومات مهمة' : 'Important Information'}
                      </h5>
                      <p className="text-sm text-gray-700">
                        {i18n.language === 'ar' 
                          ? `سيتم الرد على تذكرتك خلال ${getSLAText(formData.priority)} من وقت الإنشاء. الأولوية ${selectedPriority?.labelAr} تعني أن طلبك سيتم معالجته وفقاً لهذا الإطار الزمني.`
                          : `Your ticket will be responded to within ${getSLAText(formData.priority)} from creation. ${selectedPriority?.labelEn} priority means your request will be processed according to this timeframe.`
                        }
                      </p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Submit */}
            <div className="flex gap-4 justify-end">
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate('/tickets')}
                disabled={loading}
              >
                {i18n.language === 'ar' ? 'إلغاء' : 'Cancel'}
              </Button>
              <Button
                type="submit"
                className="btn-primary gap-2 min-w-[150px]"
                disabled={loading}
              >
                {loading ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    {i18n.language === 'ar' ? 'جاري الإنشاء...' : 'Creating...'}
                  </>
                ) : (
                  <>
                    <MessageSquare className="w-5 h-5" />
                    {i18n.language === 'ar' ? 'إنشاء التذكرة' : 'Create Ticket'}
                  </>
                )}
              </Button>
            </div>
          </form>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default CreateTicketPage;
